import { computed } from 'vue'

const DAY_COUNT = 7
const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
const ACTIVE_STATUSES = ['pending', 'in progress', 'completed']

const isScheduled = task => task.scheduledDay !== null && task.scheduledDay !== undefined &&
  task.scheduledHour !== null && task.scheduledHour !== undefined

const isCompleted = task => task.status === 'completed' || !!task.completedAt
const taskDuration = task => Math.max(0, Number(task.duration || task.duration_hours || 0))
const taskProjectId = task => task.projectId || task.bucketId || task.project_id || null

function getEnergyLevelAt(blocks = [], hour) {
  const block = blocks.find(item => hour >= item.startHour && hour < item.endHour)
  return block?.level || 'mid'
}

function inferRequiredEnergy(task) {
  const text = `${task.title || ''} ${task.description || ''}`.toLowerCase()

  if (task.energyRequirement) return task.energyRequirement
  if (taskDuration(task) >= 2) return 'high'
  if (/strategy|deep|focus|code|coding|architecture|write|solve|complex|design|proposal/.test(text)) return 'high'
  if (/admin|email|inbox|read|reading|tutorial|learn|data entry|invoice|review/.test(text)) return 'low'
  if (/routine|meeting|sync|update|organize|build|execute/.test(text)) return 'mid'

  return 'mid'
}

function fixedBlockWeeklyHours(blocks = [], visibleStart = 5, visibleEnd = 23) {
  return blocks.reduce((sum, block) => {
    const start = Number(block.startHour || 0)
    const end = Number(block.endHour || 0)
    let hours = 0

    if (start < end) {
      const visibleHours = Math.max(0, Math.min(end, visibleEnd) - Math.max(start, visibleStart))
      hours = visibleHours
    } else {
      const late = Math.max(0, visibleEnd - Math.max(start, visibleStart))
      const early = Math.max(0, Math.min(end, visibleEnd) - visibleStart)
      hours = late + early
    }

    return sum + (block.dayOfWeek === -1 ? hours * DAY_COUNT : hours)
  }, 0)
}

function projectName(projects, id, fallback = 'Unassigned') {
  const project = projects.find(item => (item._id || item.id) === id)
  return project?.name || project?.title || fallback
}

export function useWeekReviewMetrics({ tasks, projects, energyBlocks, fixedBlocks }) {
  const activeTasks = computed(() => tasks.value.filter(task => ACTIVE_STATUSES.includes(task.status)))
  const scheduledTasks = computed(() => activeTasks.value.filter(isScheduled))
  const completedScheduledTasks = computed(() => scheduledTasks.value.filter(isCompleted))
  const completedTasks = computed(() => activeTasks.value.filter(isCompleted))

  const scheduledHours = computed(() => scheduledTasks.value.reduce((sum, task) => sum + taskDuration(task), 0))
  const completedScheduledHours = computed(() => completedScheduledTasks.value.reduce((sum, task) => sum + taskDuration(task), 0))

  const executionRate = computed(() => {
    const planned = scheduledTasks.value.length
    const completed = completedScheduledTasks.value.length
    const unfinished = Math.max(planned - completed, 0)
    const rate = planned ? Math.round((completed / planned) * 100) : 0

    return {
      planned,
      completed,
      unfinished,
      plannedHours: scheduledHours.value,
      completedHours: completedScheduledHours.value,
      rate,
      insight: planned
        ? (rate < 60 ? 'Stop planning so much. Reduce your usable time next week.' : 'Your weekly plan is close to your real execution capacity.')
        : 'Plan your first week to unlock your Capacity Reality.',
    }
  })

  const energySync = computed(() => {
    const candidates = completedScheduledTasks.value
    const matches = candidates.filter(task => getEnergyLevelAt(energyBlocks.value, Number(task.scheduledHour)) === inferRequiredEnergy(task))
    const failedLowEnergyHighWork = scheduledTasks.value.filter(task => (
      !isCompleted(task) &&
      inferRequiredEnergy(task) === 'high' &&
      getEnergyLevelAt(energyBlocks.value, Number(task.scheduledHour)) === 'low'
    )).length
    const score = candidates.length ? Math.round((matches.length / candidates.length) * 100) : 0

    return {
      score,
      matches: matches.length,
      total: candidates.length,
      failedLowEnergyHighWork,
      insight: candidates.length
        ? `You have a ${score}% Energy Sync. ${failedLowEnergyHighWork ? `${failedLowEnergyHighWork} unfinished deep-work tasks were placed in low energy hours.` : 'Your hardest work is mostly aligned with your biological peaks.'}`
        : 'Complete scheduled tasks to calculate your Energy Sync.',
    }
  })

  const usableTime = computed(() => {
    const visibleWeeklyHours = 18 * DAY_COUNT
    const fixedHours = fixedBlockWeeklyHours(fixedBlocks.value)
    const usableHours = Math.max(visibleWeeklyHours - fixedHours, 0)

    return {
      fixedHours,
      usableHours,
      visibleWeeklyHours,
    }
  })

  const capacityGrowLine = computed(() => {
    const completedByDay = DAY_LABELS.map((label, day) => ({
      day,
      label,
      hours: completedTasks.value
        .filter(task => Number(task.scheduledDay) === day)
        .reduce((sum, task) => sum + taskDuration(task), 0),
    }))

    let actual = 0
    const idealStep = usableTime.value.usableHours / DAY_COUNT

    return completedByDay.map((item, index) => {
      actual += item.hours

      return {
        ...item,
        actual: Number(actual.toFixed(1)),
        ideal: Number((idealStep * (index + 1)).toFixed(1)),
      }
    })
  })

  const projectAllocation = computed(() => {
    const totals = new Map()

    completedTasks.value.forEach(task => {
      const id = taskProjectId(task) || 'unassigned'
      totals.set(id, (totals.get(id) || 0) + taskDuration(task))
    })

    const fixed = usableTime.value.fixedHours
    if (fixed) totals.set('fixed-life', fixed)

    const total = Array.from(totals.values()).reduce((sum, value) => sum + value, 0)

    return Array.from(totals.entries())
      .map(([id, hours], index) => ({
        id,
        label: id === 'fixed-life' ? 'Fixed Life Routines' : projectName(projects.value, id),
        hours: Number(hours.toFixed(1)),
        percent: total ? Math.round((hours / total) * 100) : 0,
        colorIndex: index,
      }))
      .sort((a, b) => b.hours - a.hours)
  })

  return {
    executionRate,
    energySync,
    usableTime,
    capacityGrowLine,
    projectAllocation,
  }
}
